import React from 'react';
import styled from '@emotion/styled';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import P1 from '@base/P1/P1';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import Button from '@base/Button/Button';
import Image from '@base/Image';
import H4 from '@base/H4/H4';

interface IEducatorLibraryIntroBannerProps {
  onClickIntroBanner: () => void;
}

const Wrapper = styled.div`
  background: var(--color-base-1);
  box-shadow: ${boxShadowGenerator({ color: 'rgba(60, 72, 82, 0.04)' })};
  border-radius: ${Spacings.SPACING_2B};
  padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_12B};
  margin-top: ${Spacings.SPACING_8B};
  margin-bottom: ${Spacings.SPACING_20B};
  display: flex;
  gap: ${Spacings.SPACING_6B};
  flex-direction: row;
  ${mediaQuery.uptoMobile} {
    flex-direction: row-reverse;
    padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_4B};
    gap: 0;
    margin: ${Spacings.SPACING_6B} ${Spacings.SPACING_4B};
  }
`;

const InnerWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: space-between;
  flex-direction: row;
  gap: 0;
  ${mediaQuery.uptoMobile} {
    flex-direction: column;
    gap: ${Spacings.SPACING_4B};
    align-items: start;
  }
`;

const IntroWrapper = styled.div`
  display: flex;
  gap: ${Spacings.SPACING_2};
`;

const IntroText = styled.p`
  font-size: ${Fonts.EXTRA_SMALL};
  font-weight: ${Fonts.HEAVY};
  line-height: ${Fonts.ULTRA_SMALL};
  text-transform: uppercase;
  color: var(--color-i-blue);
`;

const StyH4 = styled(H4)`
  font-weight: ${Fonts.HEAVY};
  line-height: 150%;
  margin-top: ${Spacings.SPACING_1B};
`;

const StyP1 = styled(P1)`
  font-weight: ${Fonts.SEMIBOLD};
`;

const StyButton = styled(Button)`
  font-weight: ${Fonts.HEAVY};
  font-size: ${Fonts.SMALL};
  line-height: ${Fonts.SMALL};
`;

const ImageWrapper = styled.div`
  height: ${Spacings.SPACING_18B};
  width: ${Spacings.SPACING_18B};
  span {
    width: ${Spacings.SPACING_18B} !important;
  }
`;

const EducatorLibraryIntroBanner: React.FC<
  IEducatorLibraryIntroBannerProps
> = ({ onClickIntroBanner }) => {
  return (
    <Wrapper>
      <ImageWrapper>
        <Image
          src="educator/educator-library-banner.png"
          alt="banner"
          height={Spacings.SPACING_18B}
          width={Spacings.SPACING_18B}
        />
      </ImageWrapper>
      <InnerWrapper>
        <div>
          <IntroWrapper>
            <IntroText>Introducing</IntroText>
            <Image
              src="educator/star.png"
              alt="star"
              height={Spacings.SPACING_3B}
              width={Spacings.SPACING_3B}
            />
          </IntroWrapper>
          <StyH4 color="var(--color-text-primary)">Educator library</StyH4>
          <StyP1 color="var(--color-text-secondary)">
            Entire syllabus lessons created by the educator
          </StyP1>
        </div>
        <StyButton
          // @ts-ignore
          label="View now"
          variant="filled"
          color="black"
          maxHeight="sm"
          width="141px"
          onClick={onClickIntroBanner}
        />
      </InnerWrapper>
    </Wrapper>
  );
};

export default EducatorLibraryIntroBanner;
